import * as Api from '@ViewModels';
import { skipToken, useQuery } from '@tanstack/react-query';
import { css } from 'aphrodite';
import * as React from 'react';
import { useImageAttachmentMutation } from '../../../queries/Attachments/useImageAttachmentMutation';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { useGetImageUrl } from '../../components/TestimonialImageEditor/hooks/useGetImageUrl';
import { ChevronLeft } from '../../components/svgs/icons/ChevronLeft';
import { ChevronRight } from '../../components/svgs/icons/ChevronRight';
import { Button } from '../Button';
import { ImageCropper } from '../ImageCropDialog';
import { isRedrawable } from './redrawableMedia';
import { s } from './styles';
import { CropOptions } from './types';

const CROP_SOURCE_QUERY_KEY = 'attachment/<id>/crop-source';
// Data URLs run to megabytes, so an image paged away from is dropped soon after rather than at the default.
const CROP_SOURCE_GC_TIME_MS = 60_000;

/**
 * Reframes the post's media one picture at a time, handing each crop back as a freshly uploaded
 * attachment.
 *
 * The cropper needs a data URL — a cross-origin attachment taints the canvas and `toBlob` then throws —
 * so the picture on screen is resolved through the attachment stream before it can be cropped.
 */
export function CropTab({
	images,
	impersonationContext,
	initialImageId,
	onCropped,
}: CropOptions & { impersonationContext?: Api.IImpersonationContext }) {
	const croppable = React.useMemo(() => (images ?? []).filter(isRedrawable), [images]);
	// Starts on the requested image, so opening the tab never downloads the first one on the way there.
	const [index, setIndex] = React.useState(() =>
		Math.max(
			croppable.findIndex(image => image.id === initialImageId),
			0
		)
	);

	const { getImageUrl } = useGetImageUrl(impersonationContext);
	const { mutateAsync: uploadCroppedImage } = useImageAttachmentMutation();

	React.useEffect(() => {
		const requested = croppable.findIndex(image => image.id === initialImageId);
		if (requested >= 0) {
			setIndex(requested);
		}
	}, [initialImageId, croppable]);

	const safeIndex = Math.min(index, Math.max(croppable.length - 1, 0));
	const current = croppable[safeIndex];

	// Keyed on the id rather than the attachment: the pool is rebuilt on every edit to the post, and
	// re-resolving the same picture would throw away a crop in progress. The bytes behind an id never
	// change, so nothing refetches it either.
	const { data: sourceUrl, isError: loadFailed } = useQuery({
		gcTime: CROP_SOURCE_GC_TIME_MS,
		queryFn: current ? () => getImageUrl(current) : skipToken,
		queryKey: [CROP_SOURCE_QUERY_KEY, impersonationContext?.account?.id, current?.id],
		retry: 1,
		staleTime: Infinity,
	});

	const step = (delta: number) => setIndex((safeIndex + delta + croppable.length) % croppable.length);

	const handleCropComplete = async (croppedFile: File) => {
		let attachment: Api.IFileAttachment;
		try {
			[attachment] = await uploadCroppedImage({
				files: [croppedFile],
				impersonationContext,
				resizeForSocialMedia: true,
			});
		} catch {
			// The cropper shows what it is given; a rejected upload result is not an Error and would
			// otherwise read as a failure to crop.
			throw new Error('Could not upload the cropped image. Please try again.');
		}
		// The crop is a new attachment with a new id, so the caller swaps it for the original rather
		// than adding it — every platform version routes media by id.
		onCropped(current, attachment);
	};

	if (!current) {
		return <div className={css(s.cropEmpty)}>Add an image to this post to crop it.</div>;
	}

	return (
		<div className={css(s.cropTab)}>
			{croppable.length > 1 ? (
				<div className={css(s.cropPager)}>
					<Button aria-label='Previous image' onClick={() => step(-1)} size='icon' type='button' variant='ghost'>
						<ChevronLeft />
					</Button>
					<span aria-live='polite' className={css(s.cropCount)}>
						{`${current.fileName} — image ${safeIndex + 1} of ${croppable.length}`}
					</span>
					<Button aria-label='Next image' onClick={() => step(1)} size='icon' type='button' variant='ghost'>
						<ChevronRight />
					</Button>
				</div>
			) : null}
			{loadFailed ? (
				<p className={css(s.cropError)} role='alert'>
					Could not load this image for cropping. Please try again.
				</p>
			) : sourceUrl ? (
				<ImageCropper
					applyLabel='Use this crop'
					fileName={current.fileName}
					imageUrl={sourceUrl}
					// Two uploads of one file resolve to the same data URL, and each still deserves a fresh crop.
					key={current.id}
					onCropComplete={handleCropComplete}
				/>
			) : (
				<div className={css(s.cropLoading)}>
					<LoadingSpinner type='large' />
				</div>
			)}
			<p className={css(s.cropNote)}>A crop replaces this image everywhere it appears in this post.</p>
		</div>
	);
}
